import { useEffect } from "react";
import type { CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useSession } from "../../lib/session";
import { fetchFleet, fetchVehiclePositions } from "../vehicles/api";
import { fetchCompanies } from "../customers/api";
import { fetchPeople } from "../people/api";
import { fetchDashboard as fetchServiceDashboard } from "../service/api";
import { fetchJobs } from "../wip/api";
import { fetchWeeklyCompliance } from "../hs/api";
import { useTodoCounts } from "../todo/useTodo";
import { buildLabel, buildUrl, isDevBuild } from "../../lib/build";
import {
  CarIcon,
  WrenchIcon,
  PersonIcon,
  ClockIcon,
  GaugeIcon,
  ShieldCheckIcon,
  ChecklistIcon,
  TrendIcon,
  MonitorIcon,
} from "../../components/icons";

interface Tile {
  key: string;
  label: string;
  to?: string;
  visible: boolean;
  /** Outstanding-item count, rendered as a badge on the tile. Only To Do
   * carries one — a number on a tile means "this many things are waiting
   * for you", and that is only true of one of them. */
  badge?: number;
  prefetch?: (qc: QueryClient) => unknown;
}

type TileKey = "todo" | "vehicles" | "sales" | "projects" | "service" | "people" | "hs" | "admin" | "kpi";

const TILE_ICON: Record<TileKey, (props: { className?: string }) => JSX.Element> = {
  todo: ChecklistIcon,
  vehicles: CarIcon,
  sales: TrendIcon,
  projects: ClockIcon,
  service: WrenchIcon,
  people: PersonIcon,
  hs: ShieldCheckIcon,
  admin: GaugeIcon,
  kpi: MonitorIcon,
};

/**
 * Same per-module colours as the sidebar (AppShell's sectionAccent) —
 * scoping --accent/--accent-soft locally means the tile icon and its
 * hover border (both already keyed off those variables) pick up the
 * module's colour for free.
 */
function tileAccent(key: string): CSSProperties {
  return { "--accent": `var(--mod-${key})`, "--accent-soft": `var(--mod-${key}-soft)` } as CSSProperties;
}

/**
 * A "pause" before a section appears isn't the JS chunk (those are a few
 * KB, near-instant even on a slow connection) — it's each page's own
 * Supabase query, a real network round trip. Warming the react-query
 * cache here, alongside the chunk, means that by the time a tap lands the
 * destination page's useQuery calls usually just resolve from cache
 * immediately rather than the page sitting there mid-fetch. That matters
 * beyond just feeling faster: a page that's actively fetching is a page
 * that's "busy" at the exact moment it might get backgrounded, which is
 * the condition most likely to trigger the reload-on-return behaviour —
 * see the AppShell/index.html history-floor and inline-background fixes.
 * Query keys/staleTime match each page's own useQuery exactly so this
 * cache entry is what that page's own call reuses, not a second fetch.
 */
function usePrefetchTiles(tiles: Tile[]) {
  const qc = useQueryClient();
  useEffect(() => {
    // tiles is already filtered to what this profile can see — a tile
    // without permission for a section was never included, so this never
    // fetches data the viewer isn't allowed to have.
    for (const t of tiles) t.prefetch?.(qc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * iOS keeps real DOM focus on the tile you tapped when a back-navigation
 * (including the edge-swipe gesture) returns to this page, and the
 * outline that produces isn't reliably gated by :focus-visible the way a
 * genuine click is — the CSS-only fix for that didn't hold. Blurring
 * explicitly forces it off regardless of how the browser classified the
 * focus. Right on release (the click event itself, no artificial wait)
 * — blur() doesn't cancel the click's own navigation, so there's nothing
 * to lose by not delaying it, and clearing focus immediately means
 * there's no window where a swipe-back could restore focus to a tile
 * that's mid-blur.
 */
function blurAfterTap(e: React.MouseEvent<HTMLElement>) {
  e.currentTarget.blur();
}

/**
 * The app's landing screen and back-navigation floor — see useHistoryFloor
 * in App.tsx.
 *
 * The outstanding-work prompts (vehicle checks, mileage readings, people
 * waiting for approval, items requiring attention) used to live here as
 * four stacked cards. They are now the To Do section, which is one tile
 * with a count on it: the same information, but it travels with you
 * instead of only being visible on the screen you came from, and Home
 * goes back to being a map of the app rather than a growing inbox.
 */
export default function Home() {
  const { profile, can } = useSession();
  const firstName = profile?.full_name?.split(" ")[0] ?? "";
  const todo = useTodoCounts();

  const tiles: Tile[] = [
    {
      key: "todo",
      label: "To Do",
      to: "/todo",
      visible: true,
      // Nothing while it loads rather than a zero that turns into a three
      // — a badge that changes after you have looked at it is worse than
      // one that arrives late.
      badge: todo.isLoading ? undefined : todo.total,
    },
    {
      key: "vehicles",
      label: "Vehicle Management",
      to: "/vehicles",
      visible: true,
      prefetch: (qc: QueryClient) => {
        import("../vehicles/FleetMap");
        qc.prefetchQuery({ queryKey: ["fleet"], queryFn: fetchFleet, staleTime: 30_000 });
        qc.prefetchQuery({ queryKey: ["vehicle-positions"], queryFn: fetchVehiclePositions, staleTime: 30_000 });
      },
    },
    {
      key: "sales",
      label: "Sales",
      to: "/sales",
      visible: can("can_view_service") || can("can_view_quotes") || can("can_view_projects"),
      prefetch: (qc: QueryClient) => {
        import("../customers/CompanyList");
        qc.prefetchQuery({ queryKey: ["companies"], queryFn: fetchCompanies, staleTime: 30_000 });
      },
    },
    {
      key: "projects",
      label: "Projects",
      to: "/projects",
      visible: can("can_view_wip"),
      prefetch: (qc: QueryClient) => {
        import("../wip/JobsList");
        qc.prefetchQuery({ queryKey: ["jobs"], queryFn: fetchJobs, staleTime: 30_000 });
      },
    },
    {
      key: "service",
      label: "Service",
      to: "/service",
      visible: can("can_view_service"),
      prefetch: (qc: QueryClient) => {
        import("../service/Dashboard");
        qc.prefetchQuery({ queryKey: ["service-dashboard"], queryFn: fetchServiceDashboard, staleTime: 30_000 });
      },
    },
    {
      key: "hs",
      label: "Health & Safety",
      to: "/hs",
      // Hidden on request -- the feature, its routes and its data are
      // untouched underneath; this is a one-line revert (back to
      // `can("can_view_hs")`) to bring the tile back.
      visible: false,
      prefetch: (qc: QueryClient) => {
        import("../hs/WeeklyChecks");
        qc.prefetchQuery({ queryKey: ["hs-weekly-compliance"], queryFn: () => fetchWeeklyCompliance(), staleTime: 30_000 });
      },
    },
    {
      key: "people",
      label: "People",
      to: "/people",
      visible: can("can_manage_people"),
      prefetch: (qc: QueryClient) => {
        import("../people/PeopleList");
        qc.prefetchQuery({ queryKey: ["people"], queryFn: fetchPeople, staleTime: 30_000 });
      },
    },
    {
      key: "admin",
      label: "Platform",
      to: "/platform",
      visible: can("can_view_platform"),
    },
    {
      key: "kpi",
      label: "Dashboards",
      to: "/kpi",
      visible: can("can_view_kpi_dashboards"),
    },
  ].filter((t) => t.visible);

  usePrefetchTiles(tiles);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Viewpoint Core</h1>
          <p>{firstName ? `Welcome, ${firstName}` : "Welcome"}</p>
        </div>
      </div>

      <div className="home-grid">
        {tiles.map((t) => {
          const Icon = TILE_ICON[t.key as TileKey];
          const badge = t.badge && t.badge > 0 ? t.badge : null;
          return t.to ? (
            <NavLink key={t.key} to={t.to} className="card home-tile" style={tileAccent(t.key)} onClick={blurAfterTap}>
              <span className="home-tile-icon" aria-hidden="true">
                <Icon className="home-tile-icon-svg" />
                {badge != null && <span className="nav-count">{badge > 99 ? "99+" : badge}</span>}
              </span>
              <span className="home-tile-label">{t.label}</span>
              {/* The badge is decoration to a screen reader -- this is the
                  part it actually reads. */}
              {badge != null && <span className="sr-only">{badge} outstanding</span>}
            </NavLink>
          ) : (
            <div key={t.key} className="card home-tile is-disabled" style={tileAccent(t.key)}>
              <span className="home-tile-icon" aria-hidden="true">
                <Icon className="home-tile-icon-svg" />
              </span>
              <span className="home-tile-label">{t.label}</span>
            </div>
          );
        })}
      </div>

      {/* Dale: "Worth having a PR no during our development? On homepage?"
          then "You need to tell me what number to look for" -- a bare
          commit SHA needed a click through to GitHub before it said
          anything useful, so this shows the actual PR number (this repo
          squash-merges, so the commit's own subject line always has it)
          and links straight to that PR. A dev-period convenience, not a
          permanent fixture -- one muted line under the tiles. */}
      <p className="small muted" style={{ marginTop: 16 }}>
        {isDevBuild ? (
          "Build: dev"
        ) : (
          <a href={buildUrl()!} target="_blank" rel="noreferrer noopener" className="muted">
            Build {buildLabel()}
          </a>
        )}
      </p>
    </>
  );
}
